import React, { useEffect, useMemo, useState } from 'react';
import { APP_PRODUCT_NAME } from '../config/brandingConfig';
import { isInsecureDevOrigin, isSecureContext } from '../config/pwaDevConfig';
import './PwaInstallPrompt.css';

const DISMISS_KEY = 'cc_pwa_install_dismissed';

const isStandaloneDisplay = () => (
  window.matchMedia('(display-mode: standalone)').matches
  || window.matchMedia('(display-mode: fullscreen)').matches
  || window.navigator.standalone === true
);

const isIosSafari = () => {
  const ua = window.navigator.userAgent || '';
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return isIos && isSafari;
};

const PwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1',
  );
  const [installing, setInstalling] = useState(false);
  const [showManualHint, setShowManualHint] = useState(false);

  const standalone = useMemo(() => isStandaloneDisplay(), []);
  const showIosHint = useMemo(
    () => !standalone && !dismissed && isIosSafari() && isSecureContext(),
    [standalone, dismissed],
  );

  useEffect(() => {
    if (isInsecureDevOrigin() || standalone || dismissed) return undefined;

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShowManualHint(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, [standalone, dismissed]);

  useEffect(() => {
    if (!isSecureContext() || standalone || dismissed || deferredPrompt) return undefined;
    const timer = window.setTimeout(() => setShowManualHint(true), 2500);
    return () => window.clearTimeout(timer);
  }, [standalone, dismissed, deferredPrompt]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
    setDeferredPrompt(null);
    setShowManualHint(false);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        setDeferredPrompt(null);
        dismiss();
      }
    } finally {
      setInstalling(false);
    }
  };

  if (isInsecureDevOrigin() || standalone || dismissed) return null;

  if (showIosHint) {
    return (
      <div className="pwa-install-banner" role="region" aria-label="Install app">
        <div className="pwa-install-banner__text">
          <strong>Install {APP_PRODUCT_NAME}</strong>
          <span>Tap Share, then &quot;Add to Home Screen&quot; to install on your iPhone or iPad.</span>
        </div>
        <button type="button" className="pwa-install-banner__dismiss" onClick={dismiss} aria-label="Dismiss">
          ×
        </button>
      </div>
    );
  }

  if (deferredPrompt) {
    return (
      <div className="pwa-install-banner" role="region" aria-label="Install app">
        <div className="pwa-install-banner__text">
          <strong>Install {APP_PRODUCT_NAME}</strong>
          <span>Add to your home screen for quick access like a native app.</span>
        </div>
        <div className="pwa-install-banner__actions">
          <button type="button" className="pwa-install-banner__btn pwa-install-banner__btn--primary" onClick={handleInstall} disabled={installing}>
            {installing ? 'Installing…' : 'Install'}
          </button>
          <button type="button" className="pwa-install-banner__btn" onClick={dismiss}>
            Not now
          </button>
        </div>
      </div>
    );
  }

  if (showManualHint && isSecureContext()) {
    return (
      <div className="pwa-install-banner" role="region" aria-label="Install app">
        <div className="pwa-install-banner__text">
          <strong>Install {APP_PRODUCT_NAME}</strong>
          <span>
            In Chrome: open the menu (⋮) → <strong>{`Install ${APP_PRODUCT_NAME}…`}</strong>
            {' '}or look for the install icon in the address bar.
          </span>
        </div>
        <button type="button" className="pwa-install-banner__dismiss" onClick={dismiss} aria-label="Dismiss">
          ×
        </button>
      </div>
    );
  }

  return null;
};

export default PwaInstallPrompt;
